import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EmpresaSidebar } from "@/components/empresa/EmpresaSidebar";
import { useCompanyAccess } from "@/hooks/useCompanyAccess";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowLeft, ArrowRight, Target, Building2, Wallet, Users, AlertTriangle, X, Car, GraduationCap, EyeOff, Clock, Crown, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { StateCitySelector } from "@/components/StateCitySelector";
import { parseCurrency, handleCurrencyInput } from "@/lib/utils";
import { isStartPlan, getDaysRemaining, hasActiveCredits, isSubscriptionPaid, canCreateJob, canUseFeatured } from "@/lib/planUtils";
const AREAS = [{
  value: "ti",
  label: "TI / Tecnologia"
}, {
  value: "rh",
  label: "Recursos Humanos"
}, {
  value: "marketing",
  label: "Marketing"
}, {
  value: "financeiro",
  label: "Financeiro"
}, {
  value: "comercial",
  label: "Comercial / Vendas"
}, {
  value: "operacoes",
  label: "Operações"
}, {
  value: "juridico",
  label: "Jurídico"
}, {
  value: "administrativo",
  label: "Administrativo"
}, {
  value: "logistica",
  label: "Logística"
}, {
  value: "producao",
  label: "Produção / Industrial"
}, {
  value: "atendimento",
  label: "Atendimento ao Cliente"
}, {
  value: "outros",
  label: "Outros"
}];
const WORK_MODELS = [{
  value: "presencial",
  label: "Presencial"
}, {
  value: "híbrido",
  label: "Híbrido"
}, {
  value: "remoto",
  label: "Remoto"
}];
const WORK_SCHEDULES = [{
  value: "comercial",
  label: "Comercial"
}, {
  value: "turnos",
  label: "Turnos"
}, {
  value: "flexivel",
  label: "Flexível"
}, {
  value: "meio_periodo",
  label: "Meio período"
}];
const CONTRACT_TYPES = [{
  value: "clt",
  label: "CLT"
}, {
  value: "pj",
  label: "PJ"
}, {
  value: "estagio",
  label: "Estágio"
}, {
  value: "temporario",
  label: "Temporário"
}, {
  value: "freelancer",
  label: "Freelancer"
}, {
  value: "consultor",
  label: "Consultor"
}];
const BENEFITS_OPTIONS = [{
  id: "vt",
  label: "Vale-transporte"
}, {
  id: "vr",
  label: "Vale-refeição"
}, {
  id: "va",
  label: "Vale-alimentação"
}, {
  id: "plano_saude",
  label: "Plano de saúde"
}, {
  id: "plano_odonto",
  label: "Plano odontológico"
}, {
  id: "home_office",
  label: "Auxílio home-office"
}, {
  id: "educacao",
  label: "Auxílio educação"
}, {
  id: "bonus_plr",
  label: "Bônus / PLR"
}, {
  id: "gympass",
  label: "Gympass / Academia"
}, {
  id: "day_off",
  label: "Day Off (aniversário)"
}, {
  id: "outros",
  label: "Outros benefícios"
}];
const EDUCATION_LEVELS = [{
  value: "fundamental_incompleto",
  label: "Ensino Fundamental Incompleto"
}, {
  value: "fundamental_completo",
  label: "Ensino Fundamental Completo"
}, {
  value: "fundamental_cursando",
  label: "Ensino Fundamental Cursando"
}, {
  value: "medio_incompleto",
  label: "Ensino Médio Incompleto"
}, {
  value: "medio_completo",
  label: "Ensino Médio Completo"
}, {
  value: "medio_cursando",
  label: "Ensino Médio Cursando"
}, {
  value: "superior_incompleto",
  label: "Ensino Superior Incompleto"
}, {
  value: "superior_completo",
  label: "Ensino Superior Completo"
}, {
  value: "superior_cursando",
  label: "Ensino Superior Cursando"
}, {
  value: "pos_graduacao",
  label: "Pós-graduação"
}, {
  value: "mestrado",
  label: "Mestrado"
}, {
  value: "doutorado",
  label: "Doutorado"
}, {
  value: "indiferente",
  label: "Indiferente"
}];
const CNH_TYPES = [{
  value: "A",
  label: "Categoria A"
}, {
  value: "B",
  label: "Categoria B"
}, {
  value: "C",
  label: "Categoria C"
}, {
  value: "D",
  label: "Categoria D"
}, {
  value: "E",
  label: "Categoria E"
}];
const EmpresaCriarVagaPage = () => {
  const navigate = useNavigate();
  const {
    loading: accessLoading,
    companyId,
    companyProfile
  } = useCompanyAccess(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Start plan validation states
  const [hasActiveJob, setHasActiveJob] = useState(false);
  const [activeJobExpiresAt, setActiveJobExpiresAt] = useState<string | null>(null);
  const [checkingActiveJob, setCheckingActiveJob] = useState(true);
  
  // Professional plan validation states
  const [activeJobsCount, setActiveJobsCount] = useState(0);

  // Check active jobs (for Start plan limit and Professional plan limit)
  useEffect(() => {
    const checkActiveJobs = async () => {
      if (!companyId || !companyProfile) {
        setCheckingActiveJob(false);
        return;
      }
      
      const now = new Date().toISOString();
      const isProfessional = companyProfile.plan?.startsWith('profissional_');
      
      // For Professional plan: count all active jobs for limit validation
      if (isProfessional) {
        const { count } = await supabase
          .from("jobs")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId)
          .eq("is_active", true);
        
        setActiveJobsCount(count || 0);
        setCheckingActiveJob(false);
        return;
      }
      
      // For Start plan: check if already has an active job
      if (isStartPlan(companyProfile.plan)) {
        const { data: activeJobs } = await supabase
          .from("jobs")
          .select("id, expires_at")
          .eq("company_id", companyId)
          .eq("is_active", true)
          .or(`expires_at.gt.${now},expires_at.is.null`);
        
        if (activeJobs && activeJobs.length > 0) {
          setHasActiveJob(true);
          setActiveJobExpiresAt(activeJobs[0].expires_at);
        } else {
          setHasActiveJob(false);
          setActiveJobExpiresAt(null);
        }
      }
      
      setCheckingActiveJob(false);
    };
    
    if (!accessLoading) {
      checkActiveJobs();
    }
  }, [companyId, companyProfile, accessLoading]);

  // Form state - basic info
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [area, setArea] = useState("");

  // Job type flags
  const [isPcdEligible, setIsPcdEligible] = useState(false);
  const [isFirstJob, setIsFirstJob] = useState(false);
  const [isHalfPeriod, setIsHalfPeriod] = useState(false);
  const [isHomeOffice, setIsHomeOffice] = useState(false);
  const [isConfidential, setIsConfidential] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);

  // Experience preference
  const [experiencePreference, setExperiencePreference] = useState("indiferente");

  // Requirements and desirables (tags)
  const [requirements, setRequirements] = useState<string[]>([]);
  const [desirables, setDesirables] = useState<string[]>([]);
  const [newRequirement, setNewRequirement] = useState("");
  const [newDesirable, setNewDesirable] = useState("");

  // Education and CNH
  const [educationLevel, setEducationLevel] = useState("");
  const [cnhRequired, setCnhRequired] = useState<string[]>([]);

  // Work model
  const [jobTypes, setJobTypes] = useState<string[]>([]);
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [workSchedules, setWorkSchedules] = useState<string[]>([]);

  // Contract info
  const [contractTypes, setContractTypes] = useState<string[]>([]);
  const [salaryMin, setSalaryMin] = useState("");
  const [salaryMax, setSalaryMax] = useState("");
  const [salaryUndefined, setSalaryUndefined] = useState(false);
  const [benefits, setBenefits] = useState<string[]>([]);
  const [otherBenefits, setOtherBenefits] = useState("");
  
  const showLocationFields = jobTypes.includes("presencial") || jobTypes.includes("híbrido");
  const isUserStartPlan = isStartPlan(companyProfile?.plan);
  const isProfessionalPlan = companyProfile?.plan?.startsWith('profissional_');
  const userHasActiveCredits = hasActiveCredits(companyProfile?.credits, companyProfile?.credits_expires_at);
  // Allow confidential for paid plans or users with credits
  const canUseConfidential = !isUserStartPlan || userHasActiveCredits;
  // Only Professional Plan users can use featured
  const canUseFeaturedOption = canUseFeatured(companyProfile?.plan);
  const handleToggle = (value: string, currentValues: string[], setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };
  const handleAddRequirement = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newRequirement.trim()) {
      e.preventDefault();
      if (!requirements.includes(newRequirement.trim())) {
        setRequirements([...requirements, newRequirement.trim()]);
      }
      setNewRequirement("");
    }
  };
  const handleAddDesirable = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newDesirable.trim()) {
      e.preventDefault();
      if (!desirables.includes(newDesirable.trim())) {
        setDesirables([...desirables, newDesirable.trim()]);
      }
      setNewDesirable("");
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast({
        title: "Erro",
        description: "Informe o título da vaga",
        variant: "destructive"
      });
      return;
    }
    if (!description.trim()) {
      toast({
        title: "Erro",
        description: "Informe a descrição da vaga",
        variant: "destructive"
      });
      return;
    }
    if (!area) {
      toast({
        title: "Erro",
        description: "Selecione a área/setor",
        variant: "destructive"
      });
      return;
    }
    if (jobTypes.length === 0) {
      toast({
        title: "Erro",
        description: "Selecione pelo menos um modelo de trabalho",
        variant: "destructive"
      });
      return;
    }
    if (contractTypes.length === 0) {
      toast({
        title: "Erro",
        description: "Selecione pelo menos um regime de contratação",
        variant: "destructive"
      });
      return;
    }
    if (showLocationFields && (!state || !city.trim())) {
      toast({
        title: "Erro",
        description: "Informe a cidade e estado para vagas presenciais ou híbridas",
        variant: "destructive"
      });
      return;
    }
    if (!companyProfile?.is_verified) {
      toast({
        title: "Empresa não verificada",
        description: "Sua empresa precisa ser verificada para publicar vagas.",
        variant: "destructive"
      });
      return;
    }
    
    // Validate for Professional plan
    if (isProfessionalPlan) {
      // Check subscription is paid
      if (!isSubscriptionPaid(companyProfile?.subscription_paid_until)) {
        toast({
          title: "Assinatura pendente",
          description: "Seu pagamento está pendente. Regularize para publicar novas vagas.",
          variant: "destructive"
        });
        return;
      }
      
      // Check job limit
      if (!canCreateJob(companyProfile?.plan, companyProfile?.jobs_per_month, activeJobsCount)) {
        const limit = companyProfile?.jobs_per_month || 0;
        toast({
          title: "Limite de vagas atingido",
          description: `Seu plano permite ${limit} vagas ativas. Finalize uma vaga existente ou faça upgrade.`,
          variant: "destructive"
        });
        return;
      }
    }
    
    // Validate credits for Start plan users with credits
    if (isUserStartPlan && userHasActiveCredits) {
      if ((companyProfile?.credits || 0) < 1) {
        toast({
          title: "Sem créditos",
          description: "Você não tem créditos suficientes para publicar uma vaga. Adquira mais créditos ou faça upgrade do plano.",
          variant: "destructive"
        });
        return;
      }
    }
    
    setSubmitting(true);
    try {
      // Calculate expires_at:
      // - Professional plan: no expiration (null)
      // - Start plan free users: 30 days
      // - Users with credits: 90 days
      let expiresAt: string | null = null;
      if (isUserStartPlan) {
        const expireDate = new Date();
        if (userHasActiveCredits) {
          // Credit-based job: 90 days
          expireDate.setDate(expireDate.getDate() + 90);
        } else {
          // Free Start plan: 30 days
          expireDate.setDate(expireDate.getDate() + 30);
        }
        expiresAt = expireDate.toISOString();
      }
      // Professional plan: expiresAt remains null (no expiration)
      
      const {
        data,
        error
      } = await supabase.from("jobs").insert({
        company_id: companyId,
        title: title.trim(),
        description: description.trim(),
        job_type: jobTypes,
        area: area,
        state: showLocationFields ? state : null,
        city: showLocationFields ? city.trim() : null,
        neighborhood: showLocationFields && neighborhood.trim() ? neighborhood.trim() : null,
        work_schedule: workSchedules.length > 0 ? workSchedules : null,
        contract_type: contractTypes,
        salary_range_start: !salaryUndefined && salaryMin ? parseCurrency(salaryMin) : null,
        salary_range_end: !salaryUndefined && salaryMax ? parseCurrency(salaryMax) : null,
        benefits: benefits.length > 0 ? benefits : null,
        is_active: true,
        is_pcd_eligible: isPcdEligible,
        is_first_job: isFirstJob,
        is_half_period: isHalfPeriod,
        is_home_office: isHomeOffice,
        is_confidential: canUseConfidential ? isConfidential : false,
        is_featured: canUseFeaturedOption ? isFeatured : false,
        experience_preference: experiencePreference,
        requirements: requirements.length > 0 ? requirements : null,
        desirables: desirables.length > 0 ? desirables : null,
        education_level: educationLevel || null,
        cnh_required: cnhRequired.length > 0 ? cnhRequired : null,
        other_benefits: benefits.includes("outros") && otherBenefits.trim() ? otherBenefits.trim() : null,
        expires_at: expiresAt
      }).select("id").single();
      if (error) throw error;
      
      // Deduct 1 credit after successful job creation for credit-based users
      if (isUserStartPlan && userHasActiveCredits && companyProfile?.credits) {
        const { error: creditError } = await supabase
          .from("company_profiles")
          .update({ credits: companyProfile.credits - 1 })
          .eq("id", companyId);
        
        if (creditError) {
          console.error("Error deducting credit:", creditError);
          // Don't fail the operation, job was already created
        }
      }
      
      toast({
        title: "Vaga salva!",
        description: userHasActiveCredits 
          ? `1 crédito consumido. Restam ${(companyProfile?.credits || 1) - 1} créditos.`
          : "Agora adicione perguntas para os candidatos."
      });
      navigate(`/empresa/vaga/${data.id}/perguntas`);
    } catch (error: any) {
      console.error("Error creating job:", error);
      toast({
        title: "Erro ao salvar vaga",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };
  if (accessLoading || checkingActiveJob) {
    return <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center bg-muted/30">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>;
  }

  // Block Start plan users who already have an active job
  if (isUserStartPlan && hasActiveJob) {
    const daysRemaining = activeJobExpiresAt ? getDaysRemaining(activeJobExpiresAt) : 0;
    
    return <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex">
          <EmpresaSidebar />
          <main className="flex-1 py-4 px-0 md:p-6 bg-muted/30 overflow-auto">
            <div className="mobile-container max-w-3xl md:mx-auto space-y-6 pt-14 md:pt-0">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate("/empresa/dashboard")} className="hidden sm:flex">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="text-3xl font-display font-bold">Criar Nova Vaga</h1>
              </div>

              <Card className="border-destructive/50 bg-destructive/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-destructive">
                    <Clock className="h-5 w-5" />
                    Limite do Plano Start
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    O Plano Start permite apenas <strong>1 vaga ativa por vez</strong>. 
                    Sua vaga atual expira em <strong>{daysRemaining} {daysRemaining === 1 ? 'dia' : 'dias'}</strong>.
                  </p>
                  <p className="text-muted-foreground">
                    Aguarde a expiração ou faça upgrade para o Plano Profissional para publicar mais vagas simultaneamente.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <Button variant="outline" onClick={() => navigate("/empresa/dashboard")}>
                      Voltar ao Dashboard
                    </Button>
                    <Button onClick={() => navigate("/planos")} className="gap-2">
                      <Crown className="h-4 w-4" />
                      Ver Planos
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
        <Footer />
      </div>;
  }

  return <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 flex">
        <EmpresaSidebar />
        <main className="flex-1 py-4 px-0 md:p-6 bg-muted/30 overflow-auto">
          <div className="mobile-container max-w-3xl md:mx-auto space-y-6 pt-14 md:pt-0">
            {/* Header */}
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/empresa/dashboard")} className="hidden sm:flex">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-3xl font-display font-bold">Criar Nova Vaga</h1>
            </div>

            {/* Start Plan Info Banner */}
            {isUserStartPlan && (
              <Alert className="border-primary/30 bg-primary/5">
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  <strong>Plano Start:</strong> Sua vaga ficará ativa por 30 dias. Após esse período, será automaticamente finalizada.
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Section 1: Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    Dados Básicos da Vaga
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Título do Cargo *</Label>
                    <Input id="title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex: Gerente de vendas" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Descrição das Atividades *</Label>
                    <Textarea id="description" placeholder="Descreva as principais responsabilidades e atividades do cargo..." rows={5} value={description} onChange={e => setDescription(e.target.value)} />
                  </div>

                  <div className="space-y-2">
                    <Label>Área / Setor *</Label>
                    <Select value={area} onValueChange={setArea}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a área" />
                      </SelectTrigger>
                      <SelectContent>
                        {AREAS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Section 2: Job Type */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Tipo da Vaga
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="pcd" checked={isPcdEligible} onCheckedChange={c => setIsPcdEligible(c as boolean)} />
                      <Label htmlFor="pcd" className="text-sm font-normal cursor-pointer">Vaga para PCD</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="first-job" checked={isFirstJob} onCheckedChange={c => setIsFirstJob(c as boolean)} />
                      <Label htmlFor="first-job" className="text-sm font-normal cursor-pointer">1° Emprego</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox id="home-office" checked={isHomeOffice} onCheckedChange={c => setIsHomeOffice(c as boolean)} />
                      <Label htmlFor="home-office" className="text-sm font-normal cursor-pointer">Home-office</Label>
                    </div>
                  </div>

                  {canUseConfidential && (
                    <div className="border-t border-border pt-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="confidential" className="flex items-center gap-2">
                            <EyeOff className="h-4 w-4" />
                            Vaga Confidencial
                          </Label>
                          <p className="text-sm text-muted-foreground">O nome da empresa não será exibido aos candidatos</p>
                        </div>
                        <Switch id="confidential" checked={isConfidential} onCheckedChange={setIsConfidential} />
                      </div>
                      {isConfidential && <Alert className="mt-3 bg-yellow-200 border-yellow-300 dark:bg-yellow-700/30 dark:border-yellow-700/50">
                          <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                          <AlertDescription className="text-yellow-800 dark:text-yellow-200/90">
                            A vaga será publicada como "Empresa Confidencial"
                          </AlertDescription>
                        </Alert>}
                    </div>
                  )}

                  {canUseFeaturedOption && (
                    <div className="border-t border-border pt-4 mt-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="featured" className="flex items-center gap-2">
                            <Star className="h-4 w-4 text-yellow-500" />
                            Vaga em Destaque
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Sua vaga será exibida na seção "Vagas em Destaque" da página inicial
                          </p>
                        </div>
                        <Switch id="featured" checked={isFeatured} onCheckedChange={setIsFeatured} />
                      </div>
                      {isFeatured && (
                        <Alert className="mt-3 bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800/50">
                          <Star className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                          <AlertDescription className="text-yellow-800 dark:text-yellow-200/90">
                            Esta vaga terá maior visibilidade na página inicial
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Section 3: Work Model */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    Modelo de Trabalho
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <Label>Modelo de Trabalho *</Label>
                    <div className="flex flex-wrap gap-4">
                      {WORK_MODELS.map(model => <div key={model.value} className="flex items-center space-x-2">
                          <Checkbox id={`workmodel-${model.value}`} checked={jobTypes.includes(model.value)} onCheckedChange={() => handleToggle(model.value, jobTypes, setJobTypes)} />
                          <Label htmlFor={`workmodel-${model.value}`} className="text-sm font-normal cursor-pointer">{model.label}</Label>
                        </div>)}
                    </div>
                  </div>

                  {showLocationFields && <div className="space-y-4">
                      <StateCitySelector state={state} city={city} onStateChange={value => {
                    setState(value);
                    setCity("");
                  }} onCityChange={setCity} stateLabel="Estado" cityLabel="Cidade" required />
                      <div className="space-y-2">
                        <Label>Bairro / Região</Label>
                        <Input placeholder="Opcional" value={neighborhood} onChange={e => setNeighborhood(e.target.value)} />
                      </div>
                    </div>}

                  <div className="space-y-3">
                    <Label>Horário de Trabalho</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {WORK_SCHEDULES.map(schedule => <div key={schedule.value} className="flex items-center space-x-2">
                          <Checkbox id={`schedule-${schedule.value}`} checked={workSchedules.includes(schedule.value)} onCheckedChange={() => handleToggle(schedule.value, workSchedules, setWorkSchedules)} />
                          <Label htmlFor={`schedule-${schedule.value}`} className="text-sm font-normal cursor-pointer">{schedule.label}</Label>
                        </div>)}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Section 4: Candidate Requirements */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-primary" />
                    Requisitos do Candidato
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Experience preference */}
                  <div className="space-y-3">
                    <Label>Experiência</Label>
                    <RadioGroup value={experiencePreference} onValueChange={setExperiencePreference} className="flex flex-col gap-2">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="com_experiencia" id="exp-com" />
                        <Label htmlFor="exp-com" className="font-normal cursor-pointer">Desejo candidato com experiência</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="sem_experiencia" id="exp-sem" />
                        <Label htmlFor="exp-sem" className="font-normal cursor-pointer">Desejo candidato sem experiência</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="indiferente" id="exp-ind" />
                        <Label htmlFor="exp-ind" className="font-normal cursor-pointer">Indiferente</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Education level */}
                  <div className="space-y-2">
                    <Label>Escolaridade Mínima</Label>
                    <Select value={educationLevel} onValueChange={setEducationLevel}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a escolaridade" />
                      </SelectTrigger>
                      <SelectContent>
                        {EDUCATION_LEVELS.map(level => <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* CNH */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Car className="h-4 w-4" />
                      Carteira de Habilitação (CNH)
                    </Label>
                    <div className="flex flex-wrap gap-4">
                      {CNH_TYPES.map(cnh => <div key={cnh.value} className="flex items-center space-x-2">
                          <Checkbox id={`cnh-${cnh.value}`} checked={cnhRequired.includes(cnh.value)} onCheckedChange={() => handleToggle(cnh.value, cnhRequired, setCnhRequired)} />
                          <Label htmlFor={`cnh-${cnh.value}`} className="text-sm font-normal cursor-pointer">{cnh.label}</Label>
                        </div>)}
                    </div>
                  </div>

                  {/* Requirements (tags) */}
                  <div className="space-y-3">
                    <Label>Requisitos da vaga</Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {requirements.map(req => <Badge key={req} variant="secondary" className="gap-1 pr-1">
                          {req}
                          <button type="button" onClick={() => setRequirements(requirements.filter(r => r !== req))} className="ml-1 hover:bg-muted rounded">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>)}
                    </div>
                    <Input placeholder="Digite um requisito e pressione Enter" value={newRequirement} onChange={e => setNewRequirement(e.target.value)} onKeyDown={handleAddRequirement} />
                  </div>

                  {/* Desirables (tags) */}
                  <div className="space-y-3">
                    <Label>Desejáveis (opcional)</Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {desirables.map(des => <Badge key={des} variant="outline" className="gap-1 pr-1">
                          {des}
                          <button type="button" onClick={() => setDesirables(desirables.filter(d => d !== des))} className="ml-1 hover:bg-muted rounded">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>)}
                    </div>
                    <Input placeholder="Digite um desejável e pressione Enter" value={newDesirable} onChange={e => setNewDesirable(e.target.value)} onKeyDown={handleAddDesirable} />
                  </div>
                </CardContent>
              </Card>

              {/* Section 5: Contract Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-primary" />
                    Informações Contratuais
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <Label>Regime de Contratação *</Label>
                    <div className="flex flex-wrap gap-4">
                      {CONTRACT_TYPES.map(type => <div key={type.value} className="flex items-center space-x-2">
                          <Checkbox id={`contract-${type.value}`} checked={contractTypes.includes(type.value)} onCheckedChange={() => handleToggle(type.value, contractTypes, setContractTypes)} />
                          <Label htmlFor={`contract-${type.value}`} className="text-sm font-normal cursor-pointer">{type.label}</Label>
                        </div>)}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="salary-undefined" checked={salaryUndefined} onCheckedChange={checked => {
                      setSalaryUndefined(checked as boolean);
                      if (checked) {
                        setSalaryMin("");
                        setSalaryMax("");
                      }
                    }} />
                      <Label htmlFor="salary-undefined" className="cursor-pointer">Salário a definir</Label>
                    </div>
                    {!salaryUndefined && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Salário Mínimo (R$)</Label>
                          <Input type="text" placeholder="R$ 3.000,00" value={salaryMin} onChange={e => setSalaryMin(handleCurrencyInput(e.target.value))} />
                        </div>
                        <div className="space-y-2">
                          <Label>Salário Máximo (R$)</Label>
                          <Input type="text" placeholder="R$ 5.000,00" value={salaryMax} onChange={e => setSalaryMax(handleCurrencyInput(e.target.value))} />
                        </div>
                      </div>}
                  </div>

                  <div className="space-y-3">
                    <Label>Benefícios</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {BENEFITS_OPTIONS.map(benefit => <div key={benefit.id} className="flex items-center space-x-2">
                          <Checkbox id={benefit.id} checked={benefits.includes(benefit.id)} onCheckedChange={() => handleToggle(benefit.id, benefits, setBenefits)} />
                          <Label htmlFor={benefit.id} className="text-sm font-normal cursor-pointer">{benefit.label}</Label>
                        </div>)}
                    </div>
                  </div>

                  {benefits.includes("outros") && <div className="space-y-2">
                      <Label>Descreva os outros benefícios</Label>
                      <Textarea placeholder="Ex: Auxílio creche, seguro de vida, etc." value={otherBenefits} onChange={e => setOtherBenefits(e.target.value)} rows={3} />
                    </div>}
                </CardContent>
              </Card>

              {/* Anti-discrimination warning */}
              <Alert className="bg-yellow-200 border-yellow-300 dark:bg-yellow-700/30 dark:border-yellow-700/50">
                <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                <AlertDescription className="text-yellow-800 dark:text-yellow-200/90">
                  <strong>Aviso Legal:</strong> De acordo com a Constituição Federal Brasileira (Art. 5º) e a CLT (Art. 373-A), 
                  é proibida qualquer forma de discriminação no processo seletivo por motivo de sexo, origem, raça, cor, 
                  estado civil, situação familiar, deficiência, reabilitação profissional, idade, entre outros.
                </AlertDescription>
              </Alert>

              {/* Actions */}
              <div className="flex gap-4 justify-end">
                <Button type="button" variant="outline" onClick={() => navigate("/empresa/dashboard")} disabled={submitting}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </> : <>
                      Avançar
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>}
                </Button>
              </div>
            </form>
          </div>
        </main>
      </div>
      <Footer />
    </div>;
};
export default EmpresaCriarVagaPage;